/**
 * Editor setup checklist (spec #296), shown as a tiny grey dropdown right
 * beside the editor's name at the top of the dashboard (Shawn 2026-11-01) -
 * never a full card in the page. Self-contained: loads and mutates its own
 * state via `GET/POST /api/editor/onboarding*`. Renders nothing once every
 * step reads done.
 *
 * Nothing here is ticked by hand. `calendar` ticks itself when the editor
 * opens the training calendar from the list (the subscription happens in
 * their own calendar app, which nothing can read back); telegram,
 * availability, adlingo and billing are derived by the server from data that
 * already exists elsewhere - this screen never re-derives any of them.
 *
 * @module web/screens/EditorOnboarding
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { get, post } from "../api/client.ts";
import type { EditorOnboardingChecklistResponse, EditorOnboardingStepKey } from "../api/types.ts";
import { useI18n, type MessageKey } from "../i18n/index.tsx";

/** Same AdLingo destination the dashboard's own tile already links out to. */
const ADLINGO_URL = "https://train.aditor.ai";

/** The training calendar's AddEvent page - the same calendar the mentorship tile links to. */
export const TRAINING_CALENDAR_URL = "https://www.addevent.com/calendar/vt9hlwz3yq9s";

/** The dashboard anchors its availability section with this id so the checklist can jump straight to it. */
export const AVAILABILITY_SECTION_ID = "ed-availability-section";

const STEP_LABELS: Record<EditorOnboardingStepKey, MessageKey> = {
  calendar: "onboarding.step_calendar",
  telegram: "onboarding.step_telegram",
  availability: "onboarding.step_availability",
  adlingo: "onboarding.step_adlingo",
  billing: "onboarding.step_billing",
};

const STEP_HINTS: Record<EditorOnboardingStepKey, MessageKey> = {
  calendar: "onboarding.hint_calendar",
  telegram: "onboarding.hint_telegram",
  availability: "onboarding.hint_availability",
  adlingo: "onboarding.hint_adlingo",
  billing: "onboarding.hint_billing",
};

export function SetupDropdown({
  onOpenBillingDetails,
  onOpenHandbook,
}: {
  onOpenBillingDetails: () => void;
  onOpenHandbook: () => void;
}) {
  const { t } = useI18n();
  const [checklist, setChecklist] = useState<EditorOnboardingChecklistResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setChecklist(await get<EditorOnboardingChecklistResponse>("/api/editor/onboarding"));
    } catch {
      setChecklist(null);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /** Opening the calendar is the editor's "I added it" - the step ticks itself, no checkbox. */
  async function openCalendar() {
    window.open(TRAINING_CALENDAR_URL, "_blank", "noopener,noreferrer");
    setError(null);
    try {
      setChecklist(await post<EditorOnboardingChecklistResponse>("/api/editor/onboarding/steps/calendar", { done: true }));
    } catch {
      setError("onboarding.error_save");
    }
  }

  async function connectTelegram() {
    if (telegramBusy) return;
    setTelegramBusy(true);
    setError(null);
    try {
      const resp = await post<{ url: string }>("/api/editor/onboarding/telegram-link");
      window.open(resp.url, "_blank", "noopener,noreferrer");
    } catch {
      setError("onboarding.error_telegram");
    } finally {
      setTelegramBusy(false);
    }
  }

  function jumpToAvailability() {
    setOpen(false);
    document.getElementById(AVAILABILITY_SECTION_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  if (!checklist || checklist.allDone) return null;

  const doneCount = checklist.steps.filter((s) => s.done).length;

  return (
    <div className="ed-setup" ref={wrapRef} data-section="onboarding-checklist">
      <button
        type="button"
        className="ed-setup-toggle"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t("onboarding.toggle", { done: doneCount, total: checklist.steps.length })}
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
      </button>
      {open && (
        <div className="ed-setup-panel" role="dialog" aria-label={t("onboarding.title")}>
          <div className="ed-setup-head">
            <strong>{t("onboarding.title")}</strong>
            <span className="ed-muted">{t("onboarding.progress", { done: doneCount, total: checklist.steps.length })}</span>
          </div>
          {error && <p className="editor-offer-error">{t(error)}</p>}
          <ul className="ed-onboarding-list">
            {checklist.steps.map((step) => (
              <li key={step.key} className={`ed-onboarding-step${step.done ? " ed-onboarding-done" : ""}`}>
                <div className="ed-onboarding-row">
                  <span className="ed-onboarding-dot" aria-hidden="true" />
                  <span className="ed-onboarding-label">{t(STEP_LABELS[step.key])}</span>
                </div>
                {!step.done && <p className="ed-muted ed-onboarding-hint">{t(STEP_HINTS[step.key])}</p>}

                {step.key === "calendar" && !step.done && (
                  <button type="button" className="ed-rev-resubmit" onClick={() => void openCalendar()}>{t("onboarding.calendar_cta")}</button>
                )}
                {step.key === "telegram" && !step.done && (
                  <button type="button" className="ed-rev-resubmit" disabled={telegramBusy} onClick={() => void connectTelegram()}>
                    {telegramBusy ? t("onboarding.telegram_opening") : `${t("onboarding.telegram_connect")} →`}
                  </button>
                )}
                {step.key === "availability" && !step.done && (
                  <button type="button" className="ed-rev-resubmit" onClick={jumpToAvailability}>{t("onboarding.availability_cta")}</button>
                )}
                {step.key === "adlingo" && !step.done && (
                  <a className="ed-rev-resubmit" href={ADLINGO_URL} target="_blank" rel="noopener noreferrer">{t("onboarding.adlingo_cta")}</a>
                )}
                {step.key === "billing" && !step.done && (
                  <button type="button" className="ed-rev-resubmit" onClick={() => { setOpen(false); onOpenBillingDetails(); }}>{t("onboarding.billing_cta")}</button>
                )}
              </li>
            ))}
          </ul>
          <button type="button" className="ed-footer-link" onClick={() => { setOpen(false); onOpenHandbook(); }}>
            <span>{t("onboarding.guide_link")}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
          </button>
        </div>
      )}
    </div>
  );
}
